import { useEffect, useState } from 'react';
import { ocenaRyzyka as riskApi } from '@/api/endpoints';
import type { RiskAssessmentWell } from '@/types';
import { Icon } from '@/components/Icon';

/** Funkcje studni do wyboru przy wpisywaniu ręcznym (ocena bez ujęcia). */
const FUNKCJE: { code: string; label: string }[] = [
  { code: 'PRODUCTION', label: 'Eksploatacyjna' },
  { code: 'REPLACEMENT', label: 'Zastępcza' },
  { code: 'OBSERVATION', label: 'Obserwacyjna (piezometr)' },
  { code: 'INACTIVE', label: 'Nieczynna' },
];

const pusta = { designation: '', function: 'PRODUCTION', latitude: '', longitude: '', yieldM3h: '' };

/**
 * Studnie ujęcia jako dane wejściowe oceny (art. 4f dotyczy obszaru zasilania studni, nie punktu
 * technologicznego). Dla oceny Z ujęciem lista jest read-only (studnie edytuje się na ujęciu);
 * dla oceny BEZ ujęcia — edytowalna (oznaczenie + współrzędne WGS84 per studnia).
 */
export function WellsPanel({ raId, hasIntake, canEdit, onWellsChanged }: {
  raId: number;
  hasIntake: boolean;
  canEdit: boolean;
  onWellsChanged?: (wells: RiskAssessmentWell[]) => void;
}) {
  const [wells, setWells] = useState<RiskAssessmentWell[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<typeof pusta>(pusta);
  const [editId, setEditId] = useState<number | null>(null);

  const load = () => riskApi.wells(raId)
    .then(w => { setWells(w); onWellsChanged?.(w); })
    .catch(e => setErr(e?.message ?? 'Nie udało się wczytać studni.'));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [raId]);

  const edytowalne = !hasIntake && canEdit;

  const walidujWsp = (v: string, min: number, max: number, nazwa: string): number | null | string => {
    if (v.trim() === '') return null;
    const n = Number(v.replace(',', '.'));
    if (!Number.isFinite(n)) return `${nazwa}: niepoprawna liczba`;
    if (n < min || n > max) return `${nazwa} poza zakresem WGS84 (${min}..${max})`;
    return n;
  };

  const zapisz = () => {
    if (!form.designation.trim()) { setErr('Podaj oznaczenie studni.'); return; }
    const la = walidujWsp(form.latitude, -90, 90, 'Szerokość');
    const lo = walidujWsp(form.longitude, -180, 180, 'Długość');
    if (typeof la === 'string') { setErr(la); return; }
    if (typeof lo === 'string') { setErr(lo); return; }
    if ((la === null) !== (lo === null)) { setErr('Podaj obie współrzędne albo żadną.'); return; }
    const body = {
      designation: form.designation.trim(), function: form.function || null,
      latitude: la, longitude: lo,
      yieldM3h: form.yieldM3h.trim() ? Number(form.yieldM3h.replace(',', '.')) : null,
    };
    setBusy(true); setErr(null);
    const p = editId != null ? riskApi.updateWell(raId, editId, body) : riskApi.addWell(raId, body);
    p.then(w => { setWells(w); onWellsChanged?.(w); setForm(pusta); setEditId(null); })
      .catch(e => setErr(e?.message ?? 'Nie udało się zapisać studni.'))
      .finally(() => setBusy(false));
  };

  const usun = (id: number) => {
    setBusy(true); setErr(null);
    riskApi.deleteWell(raId, id)
      .then(w => { setWells(w); onWellsChanged?.(w); })
      .catch(e => setErr(e?.message ?? 'Nie udało się usunąć studni.'))
      .finally(() => setBusy(false));
  };

  const edytuj = (w: RiskAssessmentWell) => {
    setEditId(w.id);
    setForm({
      designation: w.designation, function: w.function ?? 'PRODUCTION',
      latitude: w.latitude != null ? String(w.latitude) : '',
      longitude: w.longitude != null ? String(w.longitude) : '',
      yieldM3h: w.yieldM3h != null ? String(w.yieldM3h) : '',
    });
  };

  return (
    <div className="card"><div className="card-head">
      <span className="card-title">Studnie ujęcia ({wells.length})</span>
      <span className="text-[11px] text-ink-400 ml-2">
        {hasIntake ? 'z powiązanego ujęcia — edycja na karcie ujęcia' : 'dane wejściowe obszaru zasilania art. 4f'}
      </span>
    </div>
      <div className="card-body grid gap-2 text-[13px]">
        {err && <div className="text-red-600 text-[12px]">{err}</div>}
        {wells.length === 0 && (
          <div className="text-ink-400 text-[12px]">
            {hasIntake ? 'Powiązane ujęcie nie ma jeszcze studni.' : 'Brak studni. Dodaj oznaczenie i współrzędne otworów tworzących ujęcie.'}
          </div>
        )}
        {wells.map(w => (
          <div key={w.id} className="flex items-center gap-2 min-w-0">
            <span className="pill pill-water text-[10px] shrink-0">{w.designation}</span>
            <span className="text-ink-600 truncate min-w-0">{w.functionName ?? '—'}</span>
            <span className="text-ink-400 ml-auto whitespace-nowrap shrink-0 font-mono text-[11px]">
              {w.latitude != null && w.longitude != null
                ? `${w.latitude.toFixed(5)}, ${w.longitude.toFixed(5)}`
                : 'brak współrzędnych'}
            </span>
            {edytowalne && (
              <span className="flex items-center gap-1 shrink-0">
                <button onClick={() => edytuj(w)} disabled={busy} className="btn btn-ghost btn-sm" title="Edytuj"><Icon name="edit" className="w-3.5 h-3.5" /></button>
                <button onClick={() => usun(w.id)} disabled={busy} className="btn btn-ghost btn-sm text-red-500" title="Usuń">✕</button>
              </span>
            )}
          </div>
        ))}

        {edytowalne && (
          <div className="rounded border border-ink-100 bg-ink-50/40 p-2.5 grid gap-2 mt-1">
            <div className="text-[11px] text-ink-500">{editId != null ? 'Edycja studni' : 'Nowa studnia'}</div>
            <div className="grid grid-cols-2 gap-2">
              <input value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })}
                className="input" placeholder="Oznaczenie (np. S-1) *" />
              <select value={form.function} onChange={e => setForm({ ...form, function: e.target.value })} className="input">
                {FUNKCJE.map(f => <option key={f.code} value={f.code}>{f.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })}
                className="input" placeholder="Szerokość (lat)" inputMode="decimal" />
              <input value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })}
                className="input" placeholder="Długość (lng)" inputMode="decimal" />
              <input value={form.yieldM3h} onChange={e => setForm({ ...form, yieldM3h: e.target.value })}
                className="input" placeholder="Wydajność m³/h" inputMode="decimal" />
            </div>
            {/* „Dodaj" POD listą/formularzem — wytyczna ui-add-button-below-lists */}
            <div className="flex justify-end gap-2">
              {editId != null && <button onClick={() => { setForm(pusta); setEditId(null); }} disabled={busy} className="btn btn-ghost btn-sm">Anuluj</button>}
              <button onClick={zapisz} disabled={busy} className="btn btn-primary btn-sm">
                <Icon name="plus" className="w-3.5 h-3.5" /> {editId != null ? 'Zapisz zmiany' : 'Dodaj studnię'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
